import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {updatePanelSizeData} from '@Components/panel/panel-reducer';
import {getCurrentPage, getCurrentPageBackground} from '@Components/poster-editor/poster-editor-reducer';
import {useUpdateEffect} from 'ahooks';
import type {FillObject} from '@PosterWhiteboard/classes/fill.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {RGBA} from '@Utils/color.util';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.class';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import {Panel, PanelHeader} from '@Components/panel';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {ContextualMenu} from '@Components/contextual-menu';
import PosterEditorImageBackgroundOptions from '@Panels/poster-editor-image-background-options';
import {BackgroundTypeOptions} from './components/backgrond-type-options';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';

interface PosterEditorBackgroundTypePanelProps {
  panelId: string;
}

enum CurrentlyActiveFillPanel {
  DEFAULT = 'DEFAULT',
  COLOR_PANEL = 'COLOR_PANEL',
  IMAGE_OPTIONS = 'IMAGE_OPTIONS',
}

export default function PosterEditorBackgroundTypePanel({...props}: PosterEditorBackgroundTypePanelProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId();
  const [currentlyActivePanel, setCurrentlyActivePanel] = useState(CurrentlyActiveFillPanel.DEFAULT);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const backgroundObject = useAppSelector(getCurrentPageBackground);
  const currentPageObject = useAppSelector(getCurrentPage);
  const dispatch = useAppDispatch();
  const onClose = useDefaultPanelClose(props.panelId);
  const currentPage = currentPageObject ? window.posterEditor?.whiteboard?.pages.getPage(currentPageObject.hashedID) : undefined;

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  const getColorFillType = (): FillTypes => {
    if (!backgroundObject) {
      return FillTypes.SOLID;
    }
    if (!('fill' in backgroundObject.details)) {
      return FillTypes.SOLID;
    }

    return backgroundObject.details.fill.fillType;
  };

  const getHeightAccordingToMorphType = (): string => {
    if (backgroundObject?.details.type === BackgroundTypeName.IMAGE) {
      return '216px';
    }
    const fillType = getColorFillType();
    if (fillType === FillTypes.SOLID) {
      return '208px';
    }
    if (fillType === FillTypes.NONE) {
      return '158px';
    }
    return '256px';
  };

  const updateHeightInRedux = (): void => {
    if (currentlyActivePanel !== CurrentlyActiveFillPanel.DEFAULT) {
      return;
    }
    dispatch(
      updatePanelSizeData({
        sizeDataId: props.panelId,
        sizeData: {
          height: getHeightAccordingToMorphType(),
        },
      })
    );
  };

  useEffect(() => {
    updateHeightInRedux();
  }, []);

  if (!backgroundObject || !currentPageObject) {
    return null;
  }

  const updateFillColor = (fillColor: RGBA[], undoable = true): void => {
    void currentPage?.background.updateFromObject(
      {
        details: {
          type: BackgroundTypeName.COLOR,
          fill: {
            fillType: getColorFillType(),
            fillColor,
          },
        },
      },
      {undoable}
    );
  };

  const getColorFill = (): RGBA[] => {
    if (!('fill' in backgroundObject.details)) {
      return [[0, 0, 0, 1]];
    }
    return backgroundObject.details.fill.fillColor;
  };

  const getColorFillObject = (): FillObject => {
    return {
      fillType: getColorFillType(),
      fillColor: getColorFill(),
    };
  };

  const getDefaultContent = (): ReactElement => {
    return (
      <ContextualMenu hasMobileMorphingProperties id="background-type-mobile-options" includeLeftRightPadding={false} contentClassName="spacing-m-b-0 spacing-p-t-1 spacing-p-b-4">
        <BackgroundTypeOptions
          panelId={props.panelId}
          onOpenBackgroundImageOptions={(): void => {
            setCurrentlyActivePanel(CurrentlyActiveFillPanel.IMAGE_OPTIONS);
          }}
          onOpenColorPanel={(index: number): void => {
            setSelectedColorIndex(index);
            setCurrentlyActivePanel(CurrentlyActiveFillPanel.COLOR_PANEL);
          }}
        />
      </ContextualMenu>
    );
  };

  const getColorPicker = (): ReactElement => {
    return (
      <PosterEditorColorPanel
        onBackButton={(): void => {
          setCurrentlyActivePanel(CurrentlyActiveFillPanel.DEFAULT);
          updateHeightInRedux();
        }}
        panelId={props.panelId}
        fillObject={getColorFillObject()}
        initialSelectedColorIndex={selectedColorIndex}
        onChangeColor={updateFillColor}
        onClose={(): void => {
          onClose();
        }}
        isAlphaDisabled
      />
    );
  };

  const getHeader = (): ReactElement | null => {
    return (
      <PanelHeader
        title={window.i18next.t('pmwjs_background')}
        onClose={(): void => {
          onClose();
        }}
      />
    );
  };

  const getImageOptionsPanel = (): ReactElement => {
    return (
      <PosterEditorImageBackgroundOptions
        panelId={props.panelId}
        onBack={(): void => {
          setCurrentlyActivePanel(CurrentlyActiveFillPanel.DEFAULT);
        }}
        onClose={(): void => {
          onClose();
        }}
      />
    );
  };

  const getFillPanelContent = (): ReactElement => {
    switch (currentlyActivePanel) {
      case CurrentlyActiveFillPanel.IMAGE_OPTIONS:
        return getImageOptionsPanel();
      case CurrentlyActiveFillPanel.COLOR_PANEL:
        return getColorPicker();
      case CurrentlyActiveFillPanel.DEFAULT:
      default:
        return <Panel panelContent={getDefaultContent()} panelHeader={getHeader()} />;
    }
  };

  return getFillPanelContent();
}
